import { Actor } from "@icp-sdk/core/agent";
import { createHttpAgent } from "@motokomaker/ic-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { idlFactory } from "@/declarations/agent_gateway.did.js";
import type {
	_SERVICE,
	AgentRecord,
	AgentScope,
	ApiKeyMeta,
	HuntCombo,
	HuntTarget,
	Stats,
} from "@/declarations/agent_gateway.did.d.ts";
import { useAuth } from "@/lib/auth";
import { humanizeGatewayError, humanizeIcError } from "@/lib/ic-errors";
import { getIcConfig } from "@/lib/network";
import { toast } from "sonner";

export const AGENT_GATEWAY_CANISTER_ID =
	import.meta.env.VITE_CANISTER_ID_AGENT_GATEWAY ??
	import.meta.env.VITE_AGENT_GATEWAY_CANISTER_ID ??
	"";

export function useAgentGatewayConfigured() {
	return Boolean(AGENT_GATEWAY_CANISTER_ID);
}

async function createGatewayActor(
	identity: NonNullable<ReturnType<typeof useAuth>["identity"]>,
): Promise<_SERVICE | null> {
	if (!AGENT_GATEWAY_CANISTER_ID || !identity) return null;
	const config = getIcConfig();
	const agent = await createHttpAgent(config, identity);
	return Actor.createActor<_SERVICE>(idlFactory, {
		agent,
		canisterId: AGENT_GATEWAY_CANISTER_ID,
	});
}

export type AgentScopeName =
	| "All"
	| "Liquidate"
	| "VaultWrite"
	| "Read"
	| "MmkBook";

function scopeVariants(scopes: AgentScopeName[]): AgentScope[] {
	return scopes.map((s) => ({ [s]: null }) as AgentScope);
}

function unwrapResult<T>(r: { ok?: T; err?: unknown }, label: string): T {
	if (r && typeof r === "object" && "ok" in r && r.ok !== undefined) {
		return r.ok as T;
	}
	if (r && typeof r === "object" && "ok" in r && !("err" in r)) {
		return r.ok as T;
	}
	if (r && typeof r === "object" && "err" in r) {
		const human = humanizeGatewayError(r.err);
		console.error(`[${label}]`, r.err);
		throw new Error(human);
	}
	const human = humanizeGatewayError(r);
	console.error(`[${label}]`, r);
	throw new Error(human);
}

export function useMyAgent() {
	const { identity, isAuthenticated } = useAuth();
	return useQuery({
		queryKey: ["v2", "myAgent", identity?.getPrincipal().toText()],
		queryFn: async (): Promise<AgentRecord | null> => {
			if (!identity) return null;
			const actor = await createGatewayActor(identity);
			if (!actor) return null;
			const p = identity.getPrincipal();
			const opt = await actor.getAgent(p);
			return opt.length > 0 ? opt[0]! : null;
		},
		enabled: isAuthenticated && !!AGENT_GATEWAY_CANISTER_ID && !!identity,
		staleTime: 10_000,
	});
}

export function useMyApiKeys() {
	const { identity, isAuthenticated } = useAuth();
	return useQuery({
		queryKey: ["v2", "myApiKeys", identity?.getPrincipal().toText()],
		queryFn: async (): Promise<ApiKeyMeta[]> => {
			if (!identity) return [];
			const actor = await createGatewayActor(identity);
			if (!actor) return [];
			return actor.listApiKeyMeta();
		},
		enabled: isAuthenticated && !!AGENT_GATEWAY_CANISTER_ID && !!identity,
	});
}

export function useMyCycleCredits() {
	const { identity, isAuthenticated } = useAuth();
	return useQuery({
		queryKey: ["v2", "myCycleCredits", identity?.getPrincipal().toText()],
		queryFn: async (): Promise<bigint> => {
			if (!identity) return 0n;
			const actor = await createGatewayActor(identity);
			if (!actor) return 0n;
			return actor.myCycleCredits();
		},
		enabled: isAuthenticated && !!AGENT_GATEWAY_CANISTER_ID && !!identity,
	});
}

export function useMyHumanWaiversRemaining() {
	const { identity, isAuthenticated } = useAuth();
	return useQuery({
		queryKey: ["v2", "myWaivers", identity?.getPrincipal().toText()],
		queryFn: async (): Promise<bigint> => {
			if (!identity) return 0n;
			const actor = await createGatewayActor(identity);
			if (!actor) return 0n;
			return actor.myHumanWaiversRemaining();
		},
		enabled: isAuthenticated && !!AGENT_GATEWAY_CANISTER_ID && !!identity,
	});
}

export function useAgentGatewayStats() {
	const { identity } = useAuth();
	return useQuery({
		queryKey: ["v2", "agentGatewayStats"],
		queryFn: async (): Promise<Stats | null> => {
			if (!AGENT_GATEWAY_CANISTER_ID) return null;
			// Prefer authenticated agent when available
			if (identity) {
				const actor = await createGatewayActor(identity);
				if (actor) return actor.getStats();
			}
			const config = getIcConfig();
			const agent = await createHttpAgent(config);
			const actor = Actor.createActor<_SERVICE>(idlFactory, {
				agent,
				canisterId: AGENT_GATEWAY_CANISTER_ID,
			});
			return actor.getStats();
		},
		enabled: !!AGENT_GATEWAY_CANISTER_ID,
		staleTime: 30_000,
	});
}

export function useRegisterAgentAsHuman() {
	const { identity } = useAuth();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async ({
			name,
			scopes,
		}: {
			name: string;
			scopes: AgentScopeName[];
		}) => {
			if (!identity) throw new Error("Login required");
			const actor = await createGatewayActor(identity);
			if (!actor) throw new Error("Agent gateway not configured");
			const r = await actor.registerAgentAsHuman(name, scopeVariants(scopes));
			return unwrapResult(r as { ok: unknown }, "registerAgentAsHuman");
		},
		onSuccess: () => {
			toast.success("Agent activated");
			void qc.invalidateQueries({ queryKey: ["v2", "myAgent"] });
			void qc.invalidateQueries({ queryKey: ["v2", "agentGatewayStats"] });
			void qc.invalidateQueries({ queryKey: ["v2", "myHuntTargets"] });
			void qc.invalidateQueries({ queryKey: ["v2", "myHuntHfRange"] });
			void qc.invalidateQueries({ queryKey: ["v2", "myCollWatchlist"] });
		},
		onError: (e: Error) => toast.error(humanizeIcError(e)),
	});
}

/** Activate / deactivate personal agent (gates gateway bots; human SPA liquidate unaffected). */
export function useSetAgentActive() {
	const { identity } = useAuth();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (active: boolean) => {
			if (!identity) throw new Error("Login required");
			const actor = await createGatewayActor(identity);
			if (!actor) throw new Error("Agent gateway not configured");
			const r = await actor.setAgentActive(active);
			return unwrapResult(r as { ok: unknown }, "setAgentActive");
		},
		onSuccess: (_data, active) => {
			toast.success(active ? "Agent activated" : "Agent deactivated");
			void qc.invalidateQueries({ queryKey: ["v2", "myAgent"] });
			void qc.invalidateQueries({ queryKey: ["v2", "agentGatewayStats"] });
		},
		onError: (e: Error) => toast.error(humanizeIcError(e)),
	});
}

export function useCreateApiKey() {
	const { identity } = useAuth();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (keyLabel: string) => {
			if (!identity) throw new Error("Login required");
			const actor = await createGatewayActor(identity);
			if (!actor) throw new Error("Agent gateway not configured");
			const r = await actor.createApiKey(keyLabel);
			return unwrapResult(r as { ok: string }, "createApiKey") as string;
		},
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: ["v2", "myApiKeys"] });
		},
		onError: (e: Error) => toast.error(humanizeIcError(e)),
	});
}

export function useRevokeApiKey() {
	const { identity } = useAuth();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (keyId: bigint) => {
			if (!identity) throw new Error("Login required");
			const actor = await createGatewayActor(identity);
			if (!actor) throw new Error("Agent gateway not configured");
			const r = await actor.revokeApiKey(keyId);
			return unwrapResult(r as { ok: unknown }, "revokeApiKey");
		},
		onSuccess: () => {
			toast.success("API key revoked");
			void qc.invalidateQueries({ queryKey: ["v2", "myApiKeys"] });
		},
		onError: (e: Error) => toast.error(humanizeIcError(e)),
	});
}

export type AgentCollateral = "ICP" | "ckBTC" | "ckETH";

function toCandidCollateral(k: AgentCollateral) {
	return { [k]: null } as { ICP: null } | { ckBTC: null } | { ckETH: null };
}

function fromCandidCollateral(c: Record<string, null>): AgentCollateral {
	const k = Object.keys(c)[0] ?? "ICP";
	if (k === "ckBTC" || k === "ckETH" || k === "ICP") return k;
	return "ICP";
}

/** Browser: ICRC-2 approve ICP to gateway, then buyCycleCreditsWithIcp. */
export function useBuyCycleCreditsWithIcp() {
	const { identity, agent, refreshBalances } = useAuth();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (amountIcp: number) => {
			if (!identity || !agent) throw new Error("Login required");
			if (!AGENT_GATEWAY_CANISTER_ID) {
				throw new Error("Agent gateway not configured");
			}
			if (!Number.isFinite(amountIcp) || amountIcp < 0.01) {
				throw new Error("Minimum 0.01 ICP");
			}
			const config = getIcConfig();
			const ledgerId = config.ledgers.icp;
			if (!ledgerId) throw new Error("ICP ledger not configured");

			const { approveIcrcSpender } = await import("@motokomaker/ic-client");
			await approveIcrcSpender(
				agent,
				ledgerId,
				AGENT_GATEWAY_CANISTER_ID,
				amountIcp,
			);

			const amountE8 = BigInt(Math.round(amountIcp * 1e8));
			const actor = await createGatewayActor(identity);
			if (!actor) throw new Error("Agent gateway not configured");
			const r = await actor.buyCycleCreditsWithIcp(amountE8);
			return unwrapResult(r as { ok: bigint }, "buyCycleCreditsWithIcp") as bigint;
		},
		onSuccess: (balance) => {
			toast.success(`Credits topped up · balance ${balance.toString()}`);
			void qc.invalidateQueries({ queryKey: ["v2", "myCycleCredits"] });
			void qc.invalidateQueries({ queryKey: ["v2", "myAgent"] });
			void refreshBalances();
		},
		onError: (e: Error) => toast.error(humanizeIcError(e)),
	});
}

export function useMyCollWatchlist() {
	const { identity, isAuthenticated } = useAuth();
	return useQuery({
		queryKey: ["v2", "myCollWatchlist", identity?.getPrincipal().toText()],
		queryFn: async (): Promise<AgentCollateral[]> => {
			if (!identity) return [];
			const actor = await createGatewayActor(identity);
			if (!actor) return [];
			const list = await actor.getMyCollWatchlist();
			return list.map((c) => fromCandidCollateral(c as Record<string, null>));
		},
		enabled: isAuthenticated && !!AGENT_GATEWAY_CANISTER_ID && !!identity,
	});
}

export function useSetCollWatchlist() {
	const { identity } = useAuth();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (assets: AgentCollateral[]) => {
			if (!identity) throw new Error("Login required");
			const actor = await createGatewayActor(identity);
			if (!actor) throw new Error("Agent gateway not configured");
			const r = await actor.setMyCollWatchlist(assets.map(toCandidCollateral));
			return unwrapResult(r as { ok: unknown }, "setMyCollWatchlist");
		},
		onSuccess: () => {
			toast.success("Watchlist saved");
			void qc.invalidateQueries({ queryKey: ["v2", "myCollWatchlist"] });
			void qc.invalidateQueries({ queryKey: ["v2", "myHuntTargets"] });
		},
		onError: (e: Error) => toast.error(humanizeIcError(e)),
	});
}

export type HuntComboId = "IcpBtc" | "EthBtc" | "IcpBtcEth";

export type HuntTargetUi = {
	kind: "asset";
	asset: AgentCollateral;
} | {
	kind: "combo";
	combo: HuntComboId;
};

function toCandidHuntTarget(t: HuntTargetUi): HuntTarget {
	if (t.kind === "asset") {
		return { Asset: toCandidCollateral(t.asset) };
	}
	return { Combo: { [t.combo]: null } as HuntCombo };
}

function fromCandidHuntTarget(t: HuntTarget): HuntTargetUi | null {
	if ("Asset" in t && t.Asset) {
		return {
			kind: "asset",
			asset: fromCandidCollateral(t.Asset as Record<string, null>),
		};
	}
	if ("Combo" in t && t.Combo) {
		const key = Object.keys(t.Combo as object)[0] as HuntComboId | undefined;
		if (key === "IcpBtc" || key === "EthBtc" || key === "IcpBtcEth") {
			return { kind: "combo", combo: key };
		}
	}
	return null;
}

export function useMyHuntTargets() {
	const { identity, isAuthenticated } = useAuth();
	return useQuery({
		queryKey: ["v2", "myHuntTargets", identity?.getPrincipal().toText()],
		queryFn: async (): Promise<HuntTargetUi[]> => {
			if (!identity) return [];
			const actor = await createGatewayActor(identity);
			if (!actor) return [];
			if (typeof actor.getMyHuntTargets === "function") {
				const list = await actor.getMyHuntTargets();
				return list
					.map((t) => fromCandidHuntTarget(t))
					.filter((x): x is HuntTargetUi => x != null);
			}
			const assets = await actor.getMyCollWatchlist();
			return assets.map((c) => ({
				kind: "asset" as const,
				asset: fromCandidCollateral(c as Record<string, null>),
			}));
		},
		enabled: isAuthenticated && !!AGENT_GATEWAY_CANISTER_ID && !!identity,
	});
}

export function useSetHuntTargets() {
	const { identity } = useAuth();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (targets: HuntTargetUi[]) => {
			if (!identity) throw new Error("Login required");
			const actor = await createGatewayActor(identity);
			if (!actor) throw new Error("Agent gateway not configured");
			if (typeof actor.setMyHuntTargets !== "function") {
				const assets = targets
					.filter((t): t is { kind: "asset"; asset: AgentCollateral } => t.kind === "asset")
					.map((t) => t.asset);
				const r = await actor.setMyCollWatchlist(assets.map(toCandidCollateral));
				return unwrapResult(r as { ok: unknown }, "setMyCollWatchlist");
			}
			const r = await actor.setMyHuntTargets(targets.map(toCandidHuntTarget));
			return unwrapResult(r as { ok: unknown }, "setMyHuntTargets");
		},
		onSuccess: () => {
			toast.success("Hunt targets saved");
			void qc.invalidateQueries({ queryKey: ["v2", "myHuntTargets"] });
			void qc.invalidateQueries({ queryKey: ["v2", "myCollWatchlist"] });
		},
		onError: (e: Error) => toast.error(humanizeIcError(e)),
	});
}

export type HuntHfRangeUi = { minE8: bigint; maxE8: bigint };

const DEFAULT_HUNT_HF: HuntHfRangeUi = {
	minE8: 100_000_000n,
	maxE8: 130_000_000n,
};

export function useMyHuntHfRange() {
	const { identity, isAuthenticated } = useAuth();
	return useQuery({
		queryKey: ["v2", "myHuntHfRange", identity?.getPrincipal().toText()],
		queryFn: async (): Promise<HuntHfRangeUi> => {
			if (!identity) return DEFAULT_HUNT_HF;
			const actor = await createGatewayActor(identity);
			if (!actor || typeof actor.getMyHuntHfRange !== "function") {
				return DEFAULT_HUNT_HF;
			}
			const r = await actor.getMyHuntHfRange();
			return {
				minE8: BigInt(r.minE8),
				maxE8: BigInt(r.maxE8),
			};
		},
		enabled: isAuthenticated && !!AGENT_GATEWAY_CANISTER_ID && !!identity,
	});
}

export function useSetHuntHfRange() {
	const { identity } = useAuth();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async ({ minE8, maxE8 }: HuntHfRangeUi) => {
			if (!identity) throw new Error("Login required");
			const actor = await createGatewayActor(identity);
			if (!actor) throw new Error("Agent gateway not configured");
			if (typeof actor.setMyHuntHfRange !== "function") {
				throw new Error("Gateway missing setMyHuntHfRange — redeploy agent_gateway");
			}
			const r = await actor.setMyHuntHfRange(minE8, maxE8);
			return unwrapResult(r as { ok: unknown }, "setMyHuntHfRange");
		},
		onSuccess: () => {
			toast.success("Hunt HF range saved");
			void qc.invalidateQueries({ queryKey: ["v2", "myHuntHfRange"] });
		},
		onError: (e: Error) => toast.error(humanizeIcError(e)),
	});
}

export type AgentCallHistoryRow = {
	id: number;
	ts: number;
	method: string;
	ok: boolean;
	cyclesCharged: number;
	detail: string;
	feeKind: "waiver" | "credits" | "cycles" | "none" | null;
};

export type AgentTxHistoryRow = {
	id: number;
	ts: number;
	kind: string;
	vaultId: number | null;
	usdmSpentE8: number;
	detail: string;
};

export type AgentHistoryPage<T> = { events: T[]; total: number };

function parseFeeKindFromDetail(
	detail: string,
): AgentCallHistoryRow["feeKind"] {
	const m = /^fee=(waiver|credits|cycles|none)(?:;|$)/.exec(detail);
	if (!m) return null;
	return m[1] as AgentCallHistoryRow["feeKind"];
}

export function useMyAgentCallHistory(offset = 0, limit = 20) {
	const { identity, isAuthenticated } = useAuth();
	return useQuery({
		queryKey: [
			"v2",
			"myAgentCallHistory",
			identity?.getPrincipal().toText(),
			offset,
			limit,
		],
		queryFn: async (): Promise<AgentHistoryPage<AgentCallHistoryRow>> => {
			if (!identity) return { events: [], total: 0 };
			const actor = await createGatewayActor(identity);
			if (!actor || typeof actor.getMyCallHistory !== "function") {
				return { events: [], total: 0 };
			}
			const page = await actor.getMyCallHistory(BigInt(offset), BigInt(limit));
			const events = (page.events ?? []).map((e) => {
				const detail = e.detail ?? "";
				return {
					id: Number(e.id),
					ts: Number(e.ts),
					method: e.method,
					ok: Boolean(e.ok),
					cyclesCharged: Number(e.cyclesCharged),
					detail,
					feeKind: parseFeeKindFromDetail(detail),
				};
			});
			return { events, total: Number(page.total ?? events.length) };
		},
		enabled: isAuthenticated && !!AGENT_GATEWAY_CANISTER_ID && !!identity,
		staleTime: 15_000,
	});
}

export function useMyAgentTxHistory(offset = 0, limit = 20) {
	const { identity, isAuthenticated } = useAuth();
	return useQuery({
		queryKey: [
			"v2",
			"myAgentTxHistory",
			identity?.getPrincipal().toText(),
			offset,
			limit,
		],
		queryFn: async (): Promise<AgentHistoryPage<AgentTxHistoryRow>> => {
			if (!identity) return { events: [], total: 0 };
			const actor = await createGatewayActor(identity);
			if (!actor || typeof actor.getMyTxHistory !== "function") {
				return { events: [], total: 0 };
			}
			const page = await actor.getMyTxHistory(BigInt(offset), BigInt(limit));
			const events = (page.events ?? []).map((e) => ({
				id: Number(e.id),
				ts: Number(e.ts),
				kind: e.kind,
				vaultId:
					e.vaultId && e.vaultId.length > 0 ? Number(e.vaultId[0]) : null,
				usdmSpentE8: Number(e.usdmSpentE8),
				detail: e.detail,
			}));
			return { events, total: Number(page.total ?? events.length) };
		},
		enabled: isAuthenticated && !!AGENT_GATEWAY_CANISTER_ID && !!identity,
		staleTime: 15_000,
	});
}

/** Client-only scan interval preference (minutes). */
export const SCAN_INTERVAL_KEY = "motokomaker.v2.agentScanIntervalMin";

export function loadScanIntervalMin(): number {
	try {
		const v = Number(localStorage.getItem(SCAN_INTERVAL_KEY));
		if ([1, 2, 5, 10, 30].includes(v)) return v;
	} catch {
		/* ignore */
	}
	return 5;
}

export function saveScanIntervalMin(min: number) {
	try {
		localStorage.setItem(SCAN_INTERVAL_KEY, String(min));
	} catch {
		/* ignore */
	}
}

/** Agent path: place MMK limit via gateway (cycle commission / waiver). */
export function useAgentPlaceLimit() {
	const { identity, agent } = useAuth();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (args: {
			side: "buy" | "sell";
			price: number;
			size: number;
		}) => {
			if (!identity || !agent) throw new Error("Login required");
			const actor = await createGatewayActor(identity);
			if (!actor) throw new Error("Agent gateway not configured");
			if (typeof actor.agentPlaceLimit !== "function") {
				throw new Error("Gateway missing agentPlaceLimit — upgrade agent_gateway");
			}
			// Settlement mode: ICRC-2 approve mmk_book before gateway placeLimitFor.
			const bookId =
				import.meta.env.VITE_CANISTER_ID_MMK_BOOK?.trim() ||
				import.meta.env.VITE_MMK_BOOK_CANISTER_ID?.trim() ||
				"";
			if (bookId) {
				const { createMmkBookActor } = await import("@/lib/mmk-book-actor");
				const { approveIcrcSpender } = await import("@motokomaker/ic-client");
				const book = createMmkBookActor(bookId, agent);
				const st = await book.getStatus();
				if (st.settlementMode) {
					const usdm = st.usdmLedger[0]?.toText() ?? "";
					const mmk = st.mmkLedger[0]?.toText() ?? "";
					if (args.side === "buy") {
						if (!usdm) throw new Error("USDM ledger missing on book");
						await approveIcrcSpender(
							agent,
							usdm,
							bookId,
							args.price * args.size,
						);
					} else {
						if (!mmk) throw new Error("MMK ledger missing on book");
						await approveIcrcSpender(agent, mmk, bookId, args.size);
					}
				}
			}
			const side =
				args.side === "buy"
					? ({ BuyMmk: null } as const)
					: ({ SellMmk: null } as const);
			const priceE8 = BigInt(Math.round(args.price * 1e8));
			const sizeE8 = BigInt(Math.round(args.size * 1e8));
			const r = await actor.agentPlaceLimit(side, priceE8, sizeE8);
			return unwrapResult(r as { ok: bigint }, "agentPlaceLimit");
		},
		onSuccess: (orderId) => {
			toast.success(`Agent limit placed · #${orderId.toString()}`);
			void qc.invalidateQueries({ queryKey: ["v2", "mmk-book"] });
			void qc.invalidateQueries({ queryKey: ["v2", "myCycleCredits"] });
			void qc.invalidateQueries({ queryKey: ["v2", "myWaivers"] });
			void qc.invalidateQueries({ queryKey: ["v2", "myAgentCallHistory"] });
		},
		onError: (e: Error) => toast.error(humanizeIcError(e)),
	});
}

/** Agent path: cancel MMK order via gateway (cycle commission / waiver). */
export function useAgentCancelOrder() {
	const { identity } = useAuth();
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (orderId: string) => {
			if (!identity) throw new Error("Login required");
			const actor = await createGatewayActor(identity);
			if (!actor) throw new Error("Agent gateway not configured");
			if (typeof actor.agentCancelOrder !== "function") {
				throw new Error("Gateway missing agentCancelOrder — upgrade agent_gateway");
			}
			const r = await actor.agentCancelOrder(BigInt(orderId));
			return unwrapResult(r as { ok: unknown }, "agentCancelOrder");
		},
		onSuccess: () => {
			toast.success("Agent order cancelled");
			void qc.invalidateQueries({ queryKey: ["v2", "mmk-book"] });
			void qc.invalidateQueries({ queryKey: ["v2", "myCycleCredits"] });
			void qc.invalidateQueries({ queryKey: ["v2", "myWaivers"] });
			void qc.invalidateQueries({ queryKey: ["v2", "myAgentCallHistory"] });
		},
		onError: (e: Error) => toast.error(humanizeIcError(e)),
	});
}
